import {
  NOTIFICATION_ENDPOINTS,
  NOTIFICATIONS_PAGE_SIZE,
  type NotificationAreaCode,
} from "@/constants/api.notifications";
import { API_URL, getAuthHeaders } from "@/shared/config";
import type {
  AppNotification,
  NotificationFilterChoices,
  NotificationSectionUnreadCounts,
  NotificationsListFilters,
  NotificationsListResponse,
} from "../types";
import { normalizeSectionUnreadCounts } from "./notificationSectionCounts";

export type FetchNotificationsOptions = NotificationsListFilters & {
  page?: number;
  pageSize?: number;
  limit?: number;
  offset?: number;
};

export type FetchNotificationsResult = {
  items: AppNotification[];
  unreadCount: number;
  hasMore: boolean;
  hasPrevious: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
};

function appendFilters(params: URLSearchParams, filters?: NotificationsListFilters) {
  if (!filters) {
    return;
  }
  const search = filters.search?.trim();
  if (search) {
    params.set("search", search);
  }
  if (filters.readStatus && filters.readStatus !== "all") {
    params.set("read_status", filters.readStatus);
  }
  if (filters.eventType) {
    params.set("event_type", filters.eventType);
  }
  if (filters.entityType) {
    params.set("entity_type", filters.entityType);
  }
  if (filters.audience) {
    params.set("audience", filters.audience);
  }
  if (filters.httpMethod) {
    params.set("http_method", filters.httpMethod);
  }
  if (filters.dateFrom) {
    params.set("date_from", filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set("date_to", filters.dateTo);
  }
  if (filters.entityId != null && filters.entityId !== "") {
    params.set("entity_id", String(filters.entityId));
  }
}

export async function fetchNotifications(
  token: string,
  buildingId: number,
  options?: FetchNotificationsOptions,
): Promise<FetchNotificationsResult> {
  const params = new URLSearchParams();
  if (options?.page != null) {
    params.set("page", String(options.page));
  }
  if (options?.pageSize != null) {
    params.set("page_size", String(options.pageSize));
  } else if (options?.limit != null) {
    params.set("limit", String(options.limit));
  }
  if (options?.offset != null) {
    params.set("offset", String(options.offset));
  }
  appendFilters(params, options);
  const query = params.toString();
  const url = `${API_URL}${NOTIFICATION_ENDPOINTS.CRUD(buildingId)}${query ? `?${query}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetchNotifications failed: ${response.status}`);
  }
  const data = (await response.json()) as NotificationsListResponse;
  return {
    items: data.fields ?? [],
    unreadCount: data.unreadCount ?? 0,
    hasMore: data.hasMore ?? false,
    hasPrevious: data.hasPrevious ?? false,
    totalCount: data.totalCount ?? (data.fields?.length ?? 0),
    page: data.page ?? 1,
    pageSize: data.pageSize ?? NOTIFICATIONS_PAGE_SIZE,
  };
}

export async function fetchSectionUnreadCounts(
  token: string,
  buildingId: number,
): Promise<NotificationSectionUnreadCounts> {
  const url = `${API_URL}${NOTIFICATION_ENDPOINTS.SECTION_COUNTS(buildingId)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetchSectionUnreadCounts failed: ${response.status}`);
  }
  const data = (await response.json()) as { counts?: Partial<NotificationSectionUnreadCounts> };
  return normalizeSectionUnreadCounts(data.counts);
}

export async function markSectionNotificationsRead(
  token: string,
  buildingId: number,
  section: NotificationAreaCode,
): Promise<number> {
  const url = `${API_URL}${NOTIFICATION_ENDPOINTS.MARK_SECTION_READ(buildingId)}`;
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: JSON.stringify({ section }),
  });
  if (!response.ok) {
    throw new Error(`markSectionNotificationsRead failed: ${response.status}`);
  }
  const data = (await response.json()) as { updated?: number };
  return data.updated ?? 0;
}

export async function fetchNotificationFilterChoices(
  token: string,
  buildingId: number,
): Promise<NotificationFilterChoices> {
  const url = `${API_URL}${NOTIFICATION_ENDPOINTS.FILTER_CHOICES(buildingId)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetchNotificationFilterChoices failed: ${response.status}`);
  }
  const data = (await response.json()) as { fields?: NotificationFilterChoices };
  return {
    eventTypes: data.fields?.eventTypes ?? [],
    entityTypes: data.fields?.entityTypes ?? [],
    audiences: data.fields?.audiences ?? ["user", "staff"],
    httpMethods: data.fields?.httpMethods ?? ["POST", "PATCH", "DELETE"],
    readStatuses: data.fields?.readStatuses ?? ["all", "unread", "read"],
  };
}

export { NOTIFICATIONS_PAGE_SIZE };

export async function markAllNotificationsRead(
  token: string,
  buildingId: number,
): Promise<number> {
  const url = `${API_URL}${NOTIFICATION_ENDPOINTS.MARK_ALL_READ(buildingId)}`;
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`markAllNotificationsRead failed: ${response.status}`);
  }
  const data = (await response.json()) as { updated?: number };
  return data.updated ?? 0;
}

export async function markNotificationRead(
  token: string,
  buildingId: number,
  notificationPk: number,
): Promise<AppNotification> {
  const url = `${API_URL}${NOTIFICATION_ENDPOINTS.MARK_READ(buildingId)}`;
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: JSON.stringify({ pk: notificationPk }),
  });
  if (!response.ok) {
    throw new Error(`markNotificationRead failed: ${response.status}`);
  }
  const data = (await response.json()) as { field?: AppNotification };
  if (!data.field) {
    throw new Error("markNotificationRead: missing field in response");
  }
  return data.field;
}

export async function deleteNotification(
  token: string,
  buildingId: number,
  notificationPk: number,
): Promise<void> {
  const url = `${API_URL}${NOTIFICATION_ENDPOINTS.CRUD(buildingId)}`;
  const response = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(token),
    body: JSON.stringify({ pk: notificationPk }),
  });
  if (!response.ok) {
    throw new Error(`deleteNotification failed: ${response.status}`);
  }
}
